/**
 * useClickoutTracking Hook
 * 
 * React hook for tracking user clickout events on offers.
 * Provides a simple interface to record when users click on offers,
 * with built-in authentication, error handling, and performance optimizations.
 * 
 * @module useClickoutTracking
 */

import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import clickoutService from '../services/clickoutService';
import { trackClickout } from '../utils/activityTracking';

/**
 * Custom hook for clickout tracking
 * @param {Object} options - Configuration options
 * @param {boolean} [options.enableDebounce=true] - Enable click debouncing to prevent spam
 * @param {number} [options.debounceMs=500] - Debounce delay in milliseconds
 * @param {boolean} [options.enableLogging=true] - Enable console logging for debugging
 * @returns {Object} Hook state and methods
 */
export const useClickoutTracking = ({
  enableDebounce = true,
  debounceMs = 500,
  enableLogging = true
} = {}) => {
  // Get user from Redux store
  const user = useSelector((state) => state.user.user);
  
  // Debounce tracking to prevent rapid multiple clicks
  const lastClickRef = useRef({});

  /**
   * Check if a click should be debounced
   * @private
   */
  const shouldDebounce = useCallback((offerId) => {
    if (!enableDebounce) return false;
    
    const now = Date.now();
    const lastClick = lastClickRef.current[offerId];
    
    if (lastClick && (now - lastClick) < debounceMs) {
      return true;
    }
    
    lastClickRef.current[offerId] = now;
    return false;
  }, [enableDebounce, debounceMs]);

  /**
   * Record a clickout event
   * @param {string} searchKey - The search key that generated the offers
   * @param {Object} offerData - Offer data from the search results
   * @param {string} offerData.id - Unique offer identifier (REQUIRED)
   * @param {string} [offerData.provider] - Booking provider/website name
   * @param {number} [offerData.price] - Offer price
   * @param {string} [offerData.currency] - Price currency
   * @param {string} [offerData.group] - ACRISS car classification code
   * @param {string} [offerData.model] - Vehicle/accommodation model name
   * @param {Object} [offerData.terms] - Insurance and terms information
   * @param {boolean} [offerData.terms.zeroExcess] - Zero excess insurance
   * @param {boolean} [offerData.terms.tireAndGlassInsuranceIncluded] - Tire and glass insurance
   * @param {Object} [searchData] - Full search data (optional, to create missing search if needed)
   * @returns {Promise<boolean>} True if successfully recorded
   */
  const recordClickout = useCallback(async (searchKey, offerData, searchData = null) => {
    try {
      // Validate required parameters
      if (!searchKey) {
        if (enableLogging) {
          console.warn('useClickoutTracking: No searchKey provided');
        }
        return false;
      }

      if (!offerData?.id) {
        if (enableLogging) {
          console.warn('useClickoutTracking: No offer ID provided');
        }
        return false;
      }

      // Check for debouncing
      if (shouldDebounce(offerData.id)) {
        if (enableLogging) {
          console.log('useClickoutTracking: Clickout debounced for offer:', offerData.id);
        }
        return false;
      }

      // Prepare clickout data from offer information
      const clickoutData = {
        offerId: offerData.id,
        site: offerData.provider || offerData.key, // key is sometimes used instead of provider
        totalPrice: offerData.payment?.renderTotal || offerData.price,
        currency: offerData.payment?.renderCurrency || offerData.currency,
        acrissCode: offerData.group,
        modelName: offerData.model
      };

      // Add insurance information if available
      if (offerData.terms) {
        clickoutData.insurances = {
          zeroExcess: !!offerData.terms.zeroExcess,
          tireAndGlass: !!offerData.terms.tireAndGlassInsuranceIncluded,
          excessRefund: !!offerData.terms.excessRefundIncluded,
          additionalDriver: !!offerData.terms.additionalDriverIncluded
        };
      }

      // Record the clickout using activity tracking (handles both auth and anonymous)
      const success = await trackClickout(searchKey, clickoutData, null, searchData);

      if (enableLogging) {
        if (success) {
          console.log('useClickoutTracking: Clickout recorded successfully:', {
            searchKey,
            offerId: offerData.id,
            provider: clickoutData.site,
            hasSearchData: !!searchData
          });
        } else {
          console.warn('useClickoutTracking: Failed to record clickout');
        }
      }

      return success;

    } catch (error) {
      // Always fail silently - clickout tracking should never break user experience
      if (enableLogging) {
        console.error('useClickoutTracking: Error in recordClickout:', error);
      }
      return false;
    }
  }, [shouldDebounce, enableLogging]);

  /**
   * Track an offer click and continue with normal click handling
   * Convenience method that combines clickout tracking with custom click handling
   * @param {string} searchKey - The search key that generated the offers
   * @param {Object} offerData - Offer data from the search results
   * @param {Function} [onClickCallback] - Optional callback to execute after tracking
   * @param {Object} [searchData] - Full search data (optional)
   * @returns {Promise<void>}
   */
  const trackAndHandle = useCallback(async (searchKey, offerData, onClickCallback, searchData = null) => {
    // Record clickout (fire and forget)
    recordClickout(searchKey, offerData, searchData);

    // Execute custom click handling if provided
    if (onClickCallback && typeof onClickCallback === 'function') {
      try {
        await onClickCallback(offerData);
      } catch (error) {
        if (enableLogging) {
          console.error('useClickoutTracking: Error in click callback:', error);
        }
      }
    }
  }, [recordClickout, enableLogging]);

  /**
   * Create a click handler function for an offer
   * Returns a function that can be directly used as an onClick handler
   * @param {string} searchKey - The search key that generated the offers
   * @param {Object} offerData - Offer data from the search results
   * @param {Function} [onClickCallback] - Optional callback to execute after tracking
   * @param {Object} [searchData] - Full search data (optional)
   * @returns {Function} Click handler function
   */
  const createClickHandler = useCallback((searchKey, offerData, onClickCallback, searchData = null) => {
    return (event) => {
      // Prevent default if it's a link and we want to handle the click custom
      if (onClickCallback) {
        event.preventDefault();
      }
      
      trackAndHandle(searchKey, offerData, onClickCallback, searchData);
    };
  }, [trackAndHandle]);

  return {
    // Main methods
    recordClickout,
    trackAndHandle,
    createClickHandler,
    
    // State information
    isAuthenticated: !!user?.id,
    userId: user?.id,
    
    // Configuration
    debounceEnabled: enableDebounce,
    loggingEnabled: enableLogging
  };
};
